<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useTrack } from 'dashboard/composables';
import { COPILOT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { useUISettings } from 'dashboard/composables/useUISettings';

import CopilotInput from './CopilotInput.vue';
import CopilotLoader from './CopilotLoader.vue';
import CopilotAgentMessage from './CopilotAgentMessage.vue';
import CopilotAssistantMessage from './CopilotAssistantMessage.vue';
import CopilotThinkingGroup from './CopilotThinkingGroup.vue';
import ToggleCopilotAssistant from './ToggleCopilotAssistant.vue';
import CopilotEmptyState from './CopilotEmptyState.vue';
import SidebarActionsHeader from 'dashboard/components-next/SidebarActionsHeader.vue';

const props = defineProps({
  messages: {
    type: Array,
    default: () => [],
  },
  conversationInboxType: {
    type: String,
    required: true,
  },
  assistants: {
    type: Array,
    default: () => [],
  },
  activeAssistant: {
    type: Object,
    default: () => ({}),
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  isAgentRunning: {
    type: Boolean,
    default: false,
  },
  isControlPending: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['sendMessage', 'control', 'setAssistant']);
const { updateUISettings } = useUISettings();
const chatContainer = ref(null);
const hasInitialScroll = ref(false);

const sendMessage = message => {
  emit('sendMessage', message);
  useTrack(COPILOT_EVENTS.SEND_MESSAGE);
};

const scrollToBottom = async () => {
  await nextTick();
  if (chatContainer.value) {
    chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
  }
};

const groupedMessages = computed(() => {
  const result = [];
  let thinkingGroup = [];

  const flushThinking = () => {
    if (!thinkingGroup.length) return;
    result.push({
      id: thinkingGroup[0].id,
      message_type: 'thinking_group',
      messages: thinkingGroup,
    });
    thinkingGroup = [];
  };

  props.messages.forEach(message => {
    if (message.message_type === 'assistant_thinking') {
      thinkingGroup.push(message);
      return;
    }

    flushThinking();
    result.push(message);
  });
  flushThinking();
  return result;
});

const isLastMessageFromAssistant = computed(() => {
  const lastMessage = groupedMessages.value[groupedMessages.value.length - 1];
  return lastMessage?.message_type === 'assistant';
});

const closeCopilotPanel = () => {
  updateUISettings({
    is_copilot_panel_open: false,
    is_contact_sidebar_open: false,
  });
};

const handleSidebarAction = action => {
  if (action === 'control') emit('control');
};

const hasAssistants = computed(() => props.assistants.length > 0);
const hasMessages = computed(() => props.messages.length > 0);
const copilotButtons = computed(() => [
  {
    key: 'control',
    icon: props.isAgentRunning ? 'i-lucide-square' : 'i-lucide-refresh-ccw',
    tooltip: props.isAgentRunning
      ? 'Остановить текущую работу'
      : 'Обновить историю',
  },
]);

watch(
  () => props.messages,
  async messages => {
    if (!messages.length) return;

    const container = chatContainer.value;
    const shouldScroll =
      !hasInitialScroll.value ||
      !container ||
      container.scrollHeight - container.scrollTop - container.clientHeight <=
        80;

    hasInitialScroll.value = true;
    if (shouldScroll) await scrollToBottom();
  },
  { deep: true, immediate: true, flush: 'pre' }
);
</script>

<template>
  <div class="flex flex-col h-full min-h-0 text-sm leading-6 w-full">
    <SidebarActionsHeader
      :title="$t('CAPTAIN.COPILOT.TITLE')"
      :buttons="copilotButtons"
      @click="handleSidebarAction"
      @close="closeCopilotPanel"
    />
    <div
      ref="chatContainer"
      class="flex-1 min-h-0 flex px-4 py-4 overflow-y-auto overflow-x-hidden items-start overscroll-contain"
    >
      <div
        v-if="isLoading"
        class="flex flex-1 min-h-40 items-center justify-center text-n-slate-10"
        role="status"
        aria-label="Загрузка истории"
      >
        <i class="i-lucide-loader-circle size-5 animate-spin" />
      </div>
      <div
        v-else-if="hasMessages"
        class="space-y-4 flex-1 flex flex-col w-full"
      >
        <template v-for="(item, index) in groupedMessages" :key="item.id">
          <CopilotAgentMessage
            v-if="item.message_type === 'user'"
            :message="item.message"
            :created-at="item.created_at"
          />
          <CopilotAssistantMessage
            v-else-if="item.message_type === 'assistant'"
            :message="item.message"
            :created-at="item.created_at"
            :is-last-message="index === groupedMessages.length - 1"
            :conversation-inbox-type="conversationInboxType"
          />
          <CopilotThinkingGroup
            v-else
            :messages="item.messages"
            :default-collapsed="isLastMessageFromAssistant"
          />
        </template>

        <CopilotLoader v-if="isAgentRunning" />
      </div>
      <CopilotEmptyState
        v-else
        :has-assistants="hasAssistants"
        @use-suggestion="sendMessage"
      />
    </div>

    <div class="mx-3 mt-px mb-2">
      <div class="flex items-center gap-2 justify-between w-full mb-1">
        <ToggleCopilotAssistant
          v-if="assistants.length > 1"
          :assistants="assistants"
          :active-assistant="activeAssistant"
          @set-assistant="$event => emit('setAssistant', $event)"
        />
        <div v-else />
      </div>
      <CopilotInput
        v-if="hasAssistants"
        class="mb-1 w-full"
        @send="sendMessage"
      />
    </div>
  </div>
</template>
